'use client';

import { Button, Drawer, Heading } from '@petsmart-ui/sparky';
import cn from 'classnames';

import { usePricePolicyContentQuery } from '@libs/common-next';
import { parseRichText } from '@libs/contentful';

import {
    StackedDrawerBackButton,
    StackedDrawerBoundary,
    type UseStackedDrawerProps,
    useStackedDrawer,
} from '../stacked-drawer-boundary';
import './PricePolicyDrawer.scss';

type PricePolicyDrawerProps = UseStackedDrawerProps & {
    showBackButton?: boolean;
};

export const PricePolicyDrawer = ({
    showBackButton = false,
    forceClosed = false,
    onOpenChange,
    onCloseAll,
}: PricePolicyDrawerProps) => {
    const { pricePolicyData } = usePricePolicyContentQuery();
    const { isActive, hasBeenRendered, headingId, handleOpen, handleBack, handleClose } = useStackedDrawer({
        forceClosed,
        onOpenChange,
        onCloseAll,
    });

    if (!pricePolicyData?.title || !pricePolicyData.content) return null;

    const { title, content } = pricePolicyData;

    return (
        <>
            <Button
                type="button"
                className="price-policy-drawer__trigger"
                iconName="info-outline"
                iconPosition="before"
                iconSize="md"
                onClick={handleOpen}
                text="Open strike-through price policy"
                variant="bare"
                hideText
                circle
                data-testid="product-price-strikethrough-button"
            />
            {hasBeenRendered && (
                <StackedDrawerBoundary>
                    <Drawer
                        align="right"
                        isActive={isActive}
                        onClose={handleClose}
                        data-testid="product-price-strikethrough-drawer"
                        className={cn('price-policy-drawer', { 'price-policy-drawer--stacked': showBackButton })}
                        ariaLabelledBy={headingId}
                    >
                        <Drawer.Header className="price-policy-drawer__header">
                            {showBackButton && (
                                <StackedDrawerBackButton
                                    className="price-policy-drawer__back"
                                    testId="price-policy-drawer-back-btn"
                                    onClick={handleBack}
                                />
                            )}
                            <Heading id={headingId} tagName="h2" size="headline" data-testid="drawer-title">
                                {title}
                            </Heading>
                        </Drawer.Header>
                        <Drawer.Body data-testid="drawer-body">{parseRichText(content)}</Drawer.Body>
                    </Drawer>
                </StackedDrawerBoundary>
            )}
        </>
    );
};
